import { useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import ExistingPatientForm from "@/components/ExistingPatientForm";
import { RelatedPages } from "@/components/RelatedPages";
import StickyMobileCta from "@/components/StickyMobileCta";
import {
  CalendarClock,
  FileCheck2,
  ClipboardList,
  Phone,
  Stethoscope,
  MessageSquareHeart,
  Brain,
  Zap,
  Pill,
  Droplets,
  ScanEye,
  Waves,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import {
  buildBreadcrumbList,
  CLINIC_REF,
  SITE_ID,
  SITE_URL,
  type Crumb,
} from "@/components/schemaConstants";

const canonicalPath = "/existing-patients";
const canonicalUrl = `${SITE_URL}${canonicalPath}`;

const PHONE_DISPLAY = "(954) 827-9793";
const PHONE_HREF = "tel:9548279793";

const crumbs: Crumb[] = [
  { name: "Home", href: "/" },
  { name: "Patients", href: "/existing-patients" },
  { name: "Existing Patients", href: "/existing-patients" },
];

const faqs = [
  {
    q: "It's been over a year since I was seen. Can I just start treatment?",
    a: "In most cases we'll ask you to come in for a follow-up visit with our psychiatrist or psychiatric nurse practitioner first. Depression, medications, and your overall health may have changed, and a current evaluation is also what insurance requires before authorizing treatment. It's usually a single visit, and we can often schedule it within the week.",
  },
  {
    q: "My insurance denied TMS before. Is it worth checking again?",
    a: "Yes. Coverage criteria, plan networks, and even the carrier your employer uses change from year to year. Many patients who were denied once are approved later, especially after additional medication trials. We'll re-check your current benefits at no cost.",
  },
  {
    q: "I changed insurance. Do I need to do anything?",
    a: "Just tell us on the form and upload or bring your new card. We'll verify the new plan and let you know what has changed for the services in your care plan.",
  },
  {
    q: "Can I do the follow-up visit by telehealth?",
    a: "Follow-up visits with the psychiatrist, nurse practitioner, and therapist are generally available by secure video for established patients in Florida. TMS and SAINT® treatments themselves are in person at our Coral Springs office.",
  },
  {
    q: "How long do I have to complete treatment once I start?",
    a: "Standard TMS is designed to run about six to nine weeks without long breaks, so we schedule it as a block. If you're planning around work, travel, or the holidays, tell us your constraints on the form and we'll build a start date that lets you finish the full course.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const services = [
  {
    icon: Stethoscope,
    title: "Psychiatric Evaluation & Medication Management",
    body: "Ongoing care with our board-certified psychiatrist and psychiatric nurse practitioner: symptom review, medication adjustments, lab monitoring where appropriate, and coordination with your other providers. Follow-up visits are available in person or by telehealth.",
  },
  {
    icon: MessageSquareHeart,
    title: "Psychotherapy",
    body: "Individual therapy with our licensed therapist, offered alongside medical treatment or on its own. Therapy is frequently part of a TMS treatment plan and is often covered separately by insurance.",
  },
  {
    icon: Brain,
    title: "TMS (Transcranial Magnetic Stimulation)",
    body: "FDA-cleared, non-invasive, medication-free treatment for depression that has not responded adequately to antidepressants. Sessions are about 20 minutes, five days a week, for roughly six weeks plus a taper. Widely covered by commercial insurance, Medicare, and the VA for treatment-resistant depression, subject to your plan's criteria.",
  },
  {
    icon: Zap,
    title: "SAINT® Accelerated TMS",
    body: "The fMRI-guided, five-day protocol developed at Stanford. Ten short sessions a day for five consecutive days, with a target chosen from your own brain scan. We are the only verified outpatient SAINT® provider on the East Coast. Most patients self-pay — self-pay typically ranges from $25,000 to $28,000; BCBS is accepted in select states, and we can advise on reimbursement.",
  },
  {
    icon: Pill,
    title: "Spravato® (Esketamine)",
    body: "In-office nasal spray treatment for treatment-resistant depression, administered and monitored by our clinical team in a supervised setting. Sessions are scheduled twice weekly during the induction phase, then taper to maintenance. Coverage varies by plan; we verify your benefits before you begin.",
  },
  {
    icon: Droplets,
    title: "Ketamine-Assisted Treatment",
    body: "IV ketamine therapy for depression, anxiety, OCD, and PTSD that has not responded to standard treatments. Administered and monitored in our office by our clinical team. A course is typically six sessions over two to three weeks. Coverage varies by plan; self-pay options are available.",
  },
  {
    icon: ScanEye,
    title: "Prism® for PTSD",
    body: "A computerized, FDA-cleared neuromodulation therapy that uses real-time fMRI neurofeedback to help patients with PTSD learn to regulate the brain circuits driving their symptoms. Non-invasive and medication-free, delivered in a series of guided sessions at our Coral Springs office.",
  },
  {
    icon: Waves,
    title: "AMPA",
    body: "An accelerated treatment pathway that combines targeted medication priming with focused neuromodulation sessions to deliver faster relief for depression and anxiety. Designed for patients who need a shorter timeline than standard TMS but may not be candidates for SAINT®. Ask our clinical team whether AMPA fits your care plan.",
  },
];

const conditionsTreated = [
  { label: "Depression", href: "/about-depression" },
  { label: "Anxiety", href: "/anxiety-treatment" },
  { label: "OCD", href: "/tms-ocd" },
  { label: "PTSD", href: "/tms-ptsd" },
];

const whyNowCards = [
  {
    title: "Insurance plans change — often without notice.",
    body: "Carriers update their coverage for TMS, psychotherapy, and psychiatric care every plan year, and employers change plans, networks, and deductibles. What was covered at your last visit may look different today, and what was denied before may now be approved. A benefits check takes us about a business day and costs you nothing.",
  },
  {
    title: "A full course of treatment takes 6 to 10 weeks.",
    body: "Standard TMS runs about six to nine weeks of weekday sessions; some treatment plans, including combined medication and therapy adjustments, take closer to ten. Starting in the fall means finishing this plan year — while the deductible you've already met is still working for you and before benefits reset on January 1.",
  },
  {
    title: "Your care plan should reflect where you are today.",
    body: "Depression changes over time, and so do the treatments available. If it has been more than a few months since we've seen you, a follow-up visit with our psychiatrist or psychiatric nurse practitioner lets us re-evaluate your symptoms, review any medication changes, and update your plan before scheduling anything.",
  },
];

const stepper = [
  {
    icon: FileCheck2,
    title: "We verify your benefits.",
    body: "Within one business day, a care coordinator contacts your insurance carrier to confirm current coverage, deductible status, and authorization requirements for the services you're interested in.",
  },
  {
    icon: ClipboardList,
    title: "Our clinical team reviews your chart.",
    body: "Your psychiatrist or psychiatric nurse practitioner looks at your history with us, your last treatment plan, and anything you've told us on the form.",
  },
  {
    icon: CalendarClock,
    title: "We call you to schedule.",
    body: "If you're cleared to begin treatment, we book your start date. If you need a follow-up visit first, we schedule that — with the psychiatrist, nurse practitioner, or therapist, as your care requires.",
  },
  {
    icon: Stethoscope,
    title: "You begin.",
    body: "With your calendar and coverage settled, you start on a timeline that lets you complete the full course.",
  },
];

const ExistingPatients = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToServices = () => {
    document
      .getElementById("our-services")
      ?.scrollIntoView({ behavior: "smooth" });
  };
  const scrollToForm = () => {
    document
      .getElementById("benefits-form")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <SEOHead
        title="Existing Patients: Benefits Check & Scheduling | Neuro Wellness"
        description="Already a Neuro Wellness patient? Request a complimentary benefits check and let us schedule your next visit or start your treatment course."
        canonical={canonicalPath}
        schemaJson={[
          buildBreadcrumbList(crumbs, canonicalPath),
          faqSchema,
          {
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            "@id": `${canonicalUrl}#webpage`,
            name: "Existing Patients: Benefits Check & Scheduling | Neuro Wellness",
            description:
              "Already a Neuro Wellness patient? Request a complimentary benefits check and let us schedule your next visit or start your treatment course.",
            url: canonicalUrl,
            provider: CLINIC_REF,
            isPartOf: { "@id": SITE_ID },
            about: [
              "Benefits Verification",
              "Treatment Scheduling",
              "Existing Patient Care",
            ],
          },
        ]}
      />
      <Navbar />
      {/* Seasonal banner — sits directly below the fixed header */}
      <div className="bg-accent text-accent-foreground pt-11 sm:pt-12">
        <div className="container-px mx-auto max-w-6xl px-4 py-2 text-center text-xs font-medium leading-snug sm:py-2.5 sm:text-base">
          Benefits reset January 1. A course started in October finishes on this
          year's deductible. Request your check today.
        </div>
      </div>
      <main className="bg-background pb-28 sm:pb-16 lg:pb-20">
        {/* HERO */}
        <section className="relative overflow-hidden">
          {/* Clinic exterior photo as a brand identifier banner */}
          <div className="relative h-44 w-full sm:h-56 md:h-64">
            <img
              src="https://vibe.filesafe.space/1787327131284421342/assets/75f94f83-f4b1-43e7-96a1-c2b58ea8de41.webp"
              srcSet="https://vibe.filesafe.space/1787327131284421342/assets/81c3eac1-592d-4dca-9ed4-5334a7e3a0d6.webp 640w, https://vibe.filesafe.space/1787327131284421342/assets/75f94f83-f4b1-43e7-96a1-c2b58ea8de41.webp 1024w, https://vibe.filesafe.space/1787327131284421342/assets/86b911b2-2001-4734-a952-612e0ef2f8b1.webp 1600w"
              sizes="100vw"
              alt="Neuro Wellness TMS Centers of America — clinic entrance in Coral Springs, FL"
              className="h-full w-full object-cover"
              loading="eager"
              width={1600}
              height={900}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-primary-deep/40 to-primary-deep/30" />
            <div className="absolute inset-0 flex items-end">
              <div className="container-px mx-auto max-w-6xl px-4 pb-3 sm:pb-5">
                <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-accent backdrop-blur-sm sm:text-sm">
                  Neuro Wellness TMS Centers of America · Coral Springs, FL
                </span>
              </div>
            </div>
          </div>

          <div className="container-px mx-auto max-w-6xl px-4 pt-6 sm:pt-10">
            <div className="grid items-start gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:gap-12">
              <div className="order-2 min-w-0 lg:order-1">
                <span className="inline-block rounded-full bg-accent/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-accent">
                  For Current &amp; Returning Patients
                </span>
                <h1 className="mt-3 font-headings text-2xl font-bold leading-tight text-primary sm:mt-4 sm:text-4xl lg:text-[2.6rem]">
                  Welcome back. Let's make sure your next step is covered.
                </h1>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:mt-5 sm:text-lg">
                  Whether you're ready to begin a treatment course we discussed,
                  it's been a while since your last visit, or you simply want to
                  know what your plan covers now, this is the place to start.
                  Fill out the short form below and our care coordinators will
                  verify your benefits, review your chart with our clinical
                  team, and reach out to schedule your appointment.
                </p>
                <div className="mt-5 flex flex-col gap-2.5 sm:mt-7 sm:flex-row sm:gap-3">
                  <Button
                    size="lg"
                    onClick={scrollToForm}
                    className="bg-gradient-accent text-accent-foreground text-sm font-bold shadow-glow hover:opacity-90 sm:text-base"
                  >
                    Request My Benefits Check &amp; Appointment
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={scrollToServices}
                    className="border-primary text-sm text-primary hover:bg-primary hover:text-primary-foreground sm:text-base"
                  >
                    See Our Services
                  </Button>
                </div>
              </div>

              <div
                id="benefits-form"
                className="order-1 min-w-0 lg:order-2 flex justify-center lg:justify-end"
              >
                <div className="w-full max-w-sm">
                  <ExistingPatientForm source="existing-patients-hero" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHY NOW */}
        <section className="container-px mx-auto max-w-6xl px-4 pt-12 sm:pt-20">
          <h2 className="font-headings text-xl font-bold text-primary sm:text-3xl">
            Why now matters
          </h2>
          <div className="mt-6 grid gap-4 sm:mt-8 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
            {whyNowCards.map((card, i) => (
              <div
                key={i}
                className="rounded-2xl border border-border bg-card p-5 shadow-soft sm:p-6"
              >
                <div className="mb-3 h-1 w-12 rounded-full bg-accent sm:mb-4" />
                <h3 className="font-headings text-sm font-bold text-primary sm:text-base">
                  {card.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {card.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* MID-SCROLL OPT-IN BAND — quick capture for high-traffic readers */}
        <section className="bg-primary-deep py-8 sm:py-10">
          <div className="container-px mx-auto max-w-5xl px-4 sm:px-6">
            <div className="flex flex-col items-center justify-between gap-5 text-center sm:flex-row sm:text-left">
              <div className="flex items-center gap-4">
                <span className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-full bg-accent/20 text-accent sm:flex">
                  <CheckCircle2 className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="font-headings text-base font-bold text-white sm:text-lg">
                    Ready to get back on schedule?
                  </h3>
                  <p className="mt-1 text-sm text-white/70 leading-relaxed">
                    One short form. Benefits verified in a business day — at no
                    cost to you.
                  </p>
                </div>
              </div>
              <div className="flex w-full flex-col gap-2.5 sm:w-auto sm:flex-row sm:gap-3">
                <Button
                  size="lg"
                  onClick={scrollToForm}
                  className="bg-gradient-accent text-accent-foreground text-sm font-bold shadow-glow hover:opacity-90 sm:text-base"
                >
                  Request My Benefits Check
                  <ArrowRight className="h-4 w-4" />
                </Button>
                <a
                  href={PHONE_HREF}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-white/30 px-5 text-sm font-bold text-white transition-colors hover:bg-white/10 sm:text-base"
                >
                  <Phone className="h-4 w-4" />
                  {PHONE_DISPLAY}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* OUR SERVICES */}
        <section
          id="our-services"
          className="container-px mx-auto max-w-6xl px-4 pt-12 sm:pt-20"
        >
          <h2 className="font-headings text-xl font-bold text-primary sm:text-3xl">
            Care for depression at every level of intensity
          </h2>
          <p className="mt-3 text-sm text-muted-foreground sm:text-base">
            Every patient's plan is built by our clinical team and may combine
            more than one of the services below.
          </p>
          <div className="mt-6 grid gap-4 sm:mt-8 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
            {services.map((svc, i) => (
              <div
                key={i}
                className="rounded-2xl border border-border bg-card p-5 shadow-soft sm:p-6"
              >
                <div className="mb-3 flex items-center gap-3 sm:mb-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/10 sm:h-11 sm:w-11">
                    <svc.icon className="h-5 w-5 text-accent" />
                  </div>
                  <h3 className="font-headings text-sm font-bold text-primary sm:text-base">
                    {svc.title}
                  </h3>
                </div>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {svc.body}
                </p>
              </div>
            ))}
          </div>

          {/* Conditions treated */}
          <div className="mt-6 rounded-2xl border border-border bg-accent/5 p-5 sm:mt-8 sm:p-6">
            <p className="font-headings text-sm font-bold uppercase tracking-wider text-accent">
              Conditions We Treat
            </p>
            <div className="mt-3 flex flex-wrap gap-2 sm:mt-4 sm:gap-3">
              {conditionsTreated.map((c) => (
                <a
                  key={c.label}
                  href={c.href}
                  className="inline-flex items-center rounded-full border border-accent/30 bg-background px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:border-accent hover:bg-accent hover:text-accent-foreground sm:px-4 sm:py-2 sm:text-sm"
                >
                  {c.label}
                </a>
              ))}
            </div>
            <p className="mt-3 text-xs text-muted-foreground sm:mt-4">
              Every treatment above may be applied to one or more of these
              conditions. Your clinical team determines the right approach based
              on your current evaluation.
            </p>
          </div>

          <p className="mt-5 text-sm font-medium text-primary sm:mt-6">
            Not sure which of these applies to you? That's what the visit is
            for. Tell us where you are and we'll recommend the right next step.
          </p>
        </section>

        {/* WHAT TO EXPECT */}
        <section className="bg-card/50 py-12 sm:py-20">
          <div className="container-px mx-auto max-w-6xl px-4">
            <h2 className="font-headings text-xl font-bold text-primary sm:text-3xl">
              What to expect
            </h2>
            <div className="mt-6 grid gap-4 sm:mt-8 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4">
              {stepper.map((step, i) => (
                <div
                  key={i}
                  className="relative rounded-2xl border border-border bg-card p-5 shadow-soft sm:p-6"
                >
                  <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-full bg-accent/15 text-sm font-bold text-accent sm:h-10 sm:w-10">
                    {i + 1}
                  </div>
                  <step.icon className="mb-2 h-5 w-5 text-accent sm:mb-3 sm:h-6 sm:w-6" />
                  <h3 className="font-headings text-sm font-bold text-primary">
                    {step.title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                    {step.body}
                  </p>
                </div>
              ))}
            </div>
            <p className="mt-5 text-xs text-muted-foreground sm:mt-6">
              Benefits verification is a courtesy service and does not guarantee
              coverage. Final determination is made by your insurance carrier.
            </p>
          </div>
        </section>

        {/* Mid-page seasonal banner */}
        <div className="bg-accent text-accent-foreground">
          <div className="container-px mx-auto max-w-6xl px-4 py-2.5 text-center text-xs font-medium leading-snug sm:py-3 sm:text-base">
            Benefits reset January 1. A course started in October finishes on
            this year's deductible. Request your check today.
          </div>
        </div>

        {/* FAQ */}
        <section className="container-px mx-auto max-w-3xl px-4 pt-12 sm:pt-20">
          <h2 className="font-headings text-xl font-bold text-primary sm:text-3xl">
            Frequently asked questions
          </h2>
          <div className="mt-6 space-y-3 sm:mt-8 sm:space-y-4">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="rounded-2xl border border-border bg-card p-5 shadow-soft sm:p-6"
              >
                <h3 className="font-headings text-sm font-bold text-primary sm:text-base">
                  {faq.q}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {faq.a}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="container-px mx-auto max-w-4xl px-4 pt-12 sm:pt-20">
          <div className="rounded-3xl border border-border bg-gradient-to-br from-card to-background p-6 text-center shadow-soft sm:p-12">
            <span className="inline-block rounded-full bg-accent/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-accent">
              Take the Next Step
            </span>
            <h2 className="mt-4 font-headings text-xl font-bold text-primary sm:text-3xl">
              Let us check your benefits and hold your spot
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground sm:mt-4 sm:text-base">
              One short form. We handle the phone calls with your insurance, our
              clinical team reviews your plan, and we reach out within one
              business day to schedule.
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-2.5 sm:mt-7 sm:flex-row sm:gap-3">
              <Button
                size="lg"
                onClick={scrollToForm}
                className="bg-gradient-accent text-accent-foreground text-sm font-bold shadow-glow hover:opacity-90 sm:text-base"
              >
                Request My Benefits Check &amp; Appointment
              </Button>
              <a
                href={PHONE_HREF}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-primary px-6 py-3 text-sm font-bold text-primary transition-colors hover:bg-primary hover:text-primary-foreground sm:w-auto sm:text-base"
              >
                <Phone className="h-5 w-5" /> Call {PHONE_DISPLAY}
              </a>
            </div>
            <p className="mt-4 text-xs text-muted-foreground sm:mt-5 sm:text-sm">
              9734 West Sample Road, 2nd Floor, Suite B, Coral Springs, FL 33065
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              Existing patients can also reach us through the patient portal or
              by calling the office directly.
            </p>
          </div>
        </section>

        {/* RELATED PAGES */}
        <section className="container-px mx-auto max-w-6xl px-4 pt-12 sm:pt-20">
          <RelatedPages
            heading="Explore Related Pages"
            links={[
              {
                title: "Cost & Insurance",
                href: "/cost-insurance",
                description: "Financing options and insurance verification",
              },
              {
                title: "SAINT® Accelerated TMS",
                href: "/saint",
                description: "FDA-cleared, 5-day fMRI-guided TMS protocol",
              },
              {
                title: "TMS Therapy",
                href: "/tms",
                description:
                  "Non-invasive TMS for treatment-resistant depression",
              },
              {
                title: "Am I a Candidate?",
                href: "/am-i-a-candidate",
                description: "Find out if TMS is right for you",
              },
              {
                title: "Contact & Directions",
                href: "/contact",
                description: "Reach us and find our Coral Springs office",
              },
            ]}
          />
        </section>
      </main>
      <Footer />
      <StickyMobileCta />
    </>
  );
};

export default ExistingPatients;
